import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, AlertTriangle, BadgePercent, Tag, MessageSquare, Crown, Check, X } from "lucide-react";
import { toast } from "sonner";

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderMarkdown(md) {
  if (!md) return "";
  let html = escapeHtml(md)
    .replace(/^### (.*$)/gim, '<h3 class="font-display text-lg font-black tracking-tight mt-6 mb-2">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="font-display text-xl font-black tracking-tight mt-8 mb-3">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="font-display text-3xl font-black tracking-tight mt-2 mb-5">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-foreground">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic text-foreground/70">$1</em>')
    .replace(/^- (.*$)/gim, '<li class="ml-4 text-foreground/75">— $1</li>')
    .replace(/\n\n/g, '</p><p class="my-3 text-foreground/75 leading-relaxed text-sm">');
  return '<p class="my-3 text-foreground/75 leading-relaxed text-sm">' + html + '</p>';
}

// -------- OFFER VIEWER --------
function OfferViewer({ data }) {
  if (!data) return <p className="text-foreground/55">No hay contenido guardado.</p>;
  const tiers = data.tiers || [];
  const recommended = data.recommended_tier;

  return (
    <Tabs defaultValue="packs">
      <TabsList className="bg-foreground/[0.04] rounded-2xl p-1 grid grid-cols-5 h-auto mt-3" data-testid="lib-offer-tabs">
        <TabsTrigger value="packs" className="rounded-xl data-[state=active]:bg-white py-2 text-xs">Packs</TabsTrigger>
        <TabsTrigger value="pricing" className="rounded-xl data-[state=active]:bg-white py-2 text-xs">Precio</TabsTrigger>
        <TabsTrigger value="risks" className="rounded-xl data-[state=active]:bg-white py-2 text-xs">Riesgos</TabsTrigger>
        <TabsTrigger value="discount" className="rounded-xl data-[state=active]:bg-white py-2 text-xs">Rebaja</TabsTrigger>
        <TabsTrigger value="message" className="rounded-xl data-[state=active]:bg-white py-2 text-xs">Mensaje</TabsTrigger>
      </TabsList>

      <TabsContent value="packs" className="mt-4 space-y-3">
        {data.positioning_statement && (
          <div className="text-sm text-foreground/75 italic border-l-2 border-foreground/15 pl-3">
            {data.positioning_statement}
          </div>
        )}
        {tiers.map((t) => {
          const isRec = t.name === recommended;
          return (
            <div key={t.name} className={`rounded-2xl p-4 border ${isRec ? "border-foreground bg-foreground/[0.02]" : "border-black/[0.08] bg-white"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-display text-lg font-black">{t.name}</h4>
                    {isRec && <span className="text-[9px] uppercase tracking-wider bg-foreground text-white px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><Crown className="w-2.5 h-2.5" /> Recomendado</span>}
                  </div>
                  {t.subtitle && <p className="text-xs text-foreground/65 mt-1">{t.subtitle}</p>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-black text-base">{t.price_range}</div>
                  <div className="text-[10px] text-foreground/55">{t.timeline}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-foreground/65">
                <div><span className="font-semibold text-foreground/80">Revisiones:</span> {t.revisions}</div>
                <div><span className="font-semibold text-foreground/80">Soporte:</span> {t.support_level}</div>
                <div><span className="font-semibold text-foreground/80">Complejidad:</span> {t.complexity_level}</div>
              </div>
              {t.includes?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {t.includes.map((x, i) => (
                    <li key={i} className="text-xs flex gap-2"><Check className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" /> {x}</li>
                  ))}
                </ul>
              )}
              {t.excludes?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {t.excludes.map((x, i) => (
                    <li key={i} className="text-xs text-foreground/55 flex gap-2"><X className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" /> {x}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </TabsContent>

      <TabsContent value="pricing" className="mt-4 space-y-4">
        {data.pricing_logic && (
          <div className="app-card p-5">
            <div className="flex items-center gap-2 mb-2"><Tag className="w-3.5 h-3.5" /> <span className="text-[10px] uppercase tracking-wider font-bold text-foreground/55">Lógica de pricing</span></div>
            <p className="text-sm leading-relaxed text-foreground/80">{data.pricing_logic}</p>
          </div>
        )}
        {data.upsells?.length > 0 && (
          <div className="app-card p-5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-3">Upsells sugeridos</div>
            <div className="space-y-2">
              {data.upsells.map((u, i) => (
                <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-black/[0.05] last:border-0">
                  <div>
                    <div className="font-semibold text-sm">{u.name}</div>
                    <div className="text-xs text-foreground/65 mt-0.5">{u.why}</div>
                  </div>
                  <div className="text-sm font-display font-black shrink-0">{u.price_range}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="risks" className="mt-4 space-y-4">
        {data.risks?.length > 0 && (
          <div className="app-card p-5">
            <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> <span className="text-[10px] uppercase tracking-wider font-bold text-foreground/55">Riesgos que debes vigilar</span></div>
            <ul className="space-y-2 text-sm">
              {data.risks.map((r, i) => <li key={i} className="leading-relaxed">— {r}</li>)}
            </ul>
          </div>
        )}
        {data.anti_scope_creep?.length > 0 && (
          <div className="app-card p-5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-3">Reglas anti scope creep</div>
            <ul className="space-y-2 text-sm">
              {data.anti_scope_creep.map((r, i) => <li key={i} className="leading-relaxed font-medium">→ {r}</li>)}
            </ul>
          </div>
        )}
      </TabsContent>

      <TabsContent value="discount" className="mt-4">
        {data.discount_response ? (
          <div className="app-card p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2"><BadgePercent className="w-3.5 h-3.5" /> <span className="text-[10px] uppercase tracking-wider font-bold text-foreground/55">Respuesta lista para enviar</span></div>
              <button onClick={() => { navigator.clipboard.writeText(data.discount_response); toast.success("Copiado"); }} className="btn-apple-outline text-xs"><Copy className="w-3 h-3" /> Copiar</button>
            </div>
            <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line">{data.discount_response}</p>
          </div>
        ) : <p className="text-sm text-foreground/55">No hay respuesta de rebaja guardada.</p>}
      </TabsContent>

      <TabsContent value="message" className="mt-4">
        {data.premium_language ? (
          <div className="app-card p-5 space-y-4">
            <div className="flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> <span className="text-[10px] uppercase tracking-wider font-bold text-foreground/55">Mensaje para el cliente</span></div>
            <div className="border-b border-black/[0.06] pb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">Asunto</span>
              <p className="mt-1 font-bold">{data.premium_language.subject}</p>
            </div>
            <div className="border-b border-black/[0.06] pb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">Apertura</span>
              <p className="mt-1 text-sm leading-relaxed">{data.premium_language.opening}</p>
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">Encuadre de valor</span>
              <p className="mt-1 text-sm leading-relaxed text-foreground/80">{data.premium_language.value_framing}</p>
            </div>
          </div>
        ) : <p className="text-sm text-foreground/55">No hay mensaje guardado.</p>}
      </TabsContent>
    </Tabs>
  );
}

// -------- CLIENT VIEWER --------
function ClientViewer({ data }) {
  if (!data) return <p className="text-foreground/55">No hay contenido guardado.</p>;
  const cps = data.client_power_score || {};
  return (
    <div className="space-y-4 mt-2">
      {cps.total !== undefined && (
        <div className="app-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55">Client Power Score</div>
              <div className="font-display font-black text-5xl mt-1">{cps.total}<span className="text-2xl text-foreground/40">/100</span></div>
            </div>
            <div className="text-right">
              <span className="tag-pill tag-pill-mint">{cps.label || ""}</span>
            </div>
          </div>
        </div>
      )}
      {data.recommended_approach && (
        <div className="app-card p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-2">Aproximación recomendada</div>
          <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line">{data.recommended_approach}</p>
        </div>
      )}
      {data.value_gap && (
        <div className="app-card p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-2">Diagnóstico de valor</div>
          <p className="font-semibold mb-1">{data.value_gap.verdict}</p>
          <p className="text-sm text-foreground/75">{data.value_gap.explanation}</p>
        </div>
      )}
      {data.red_flags?.length > 0 && (
        <div className="app-card p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-2">Red flags</div>
          <ul className="space-y-1.5 text-sm">{data.red_flags.map((r, i) => <li key={i}>🚩 {r}</li>)}</ul>
        </div>
      )}
      {data.green_flags?.length > 0 && (
        <div className="app-card p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-2">Green flags</div>
          <ul className="space-y-1.5 text-sm">{data.green_flags.map((r, i) => <li key={i}>✓ {r}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// -------- PROPOSAL VIEWER --------
function ProposalViewer({ markdown }) {
  if (!markdown) return <p className="text-foreground/55">No hay contenido guardado.</p>;
  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex">
        <button onClick={() => { navigator.clipboard.writeText(markdown); toast.success("Markdown copiado"); }} className="btn-apple-outline text-xs"><Copy className="w-3 h-3" /> Copiar markdown</button>
      </div>
      <div className="app-card p-6 max-h-[60vh] overflow-y-auto">
        <article dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }} />
      </div>
    </div>
  );
}

// -------- MAIN DIALOG --------
export default function LibraryItemDialog({ item, open, onClose }) {
  if (!item) return null;
  const payload = item.payload || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="library-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-[-0.02em] pr-8 text-left">{item.title}</DialogTitle>
          <div className="text-xs text-foreground/55 mt-1">{new Date(item.created_at).toLocaleString()}</div>
        </DialogHeader>

        {item.kind === "offer" && <OfferViewer data={payload.output} />}
        {item.kind === "client" && <ClientViewer data={payload.output} />}
        {item.kind === "proposal" && <ProposalViewer markdown={payload.markdown} />}
      </DialogContent>
    </Dialog>
  );
}
